import { useCallback } from 'react';
import { TaskHelpers } from 'ontraccr-common';
import { getIdMap, uuid } from '../../../../helpers/helpers';

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;

const addHours = (existingHours, newHours) => (
  (parseFloat(existingHours) || 0) + (parseFloat(newHours) || 0)
);

export const getHours = (value, source = "preloadSectionParser") => {
  let totalRegHours;
  let totalOtHours;
  let totalDoubleOThours;

  const {
    hourBased,
    hours = 0,
    minutes = 0,
    otHours = 0,
    otMinutes = 0,
    doubleOTHours = 0,
    doubleOTMinutes = 0,
  } = value;

  if (hourBased && source !== "timeTrackingGridFromTasks") { // Tasks don't have hours in value
    totalRegHours = addHours(hours, (minutes || 0) / 60);
    totalOtHours = addHours(otHours, (otMinutes || 0) / 60);
    totalDoubleOThours = addHours(doubleOTHours, (doubleOTMinutes || 0) / 60);
  } else {
    const {
      regularTime,
      overtime,
      doubleOT,
    } = TaskHelpers.getRuntimes(value, false);
    // Convert milliseconds to hours
    const conversionFactor = MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR;
    totalRegHours = regularTime / conversionFactor;
    totalOtHours = overtime / conversionFactor;
    totalDoubleOThours = doubleOT / conversionFactor;
  }

  return {
    totalRegHours,
    totalOtHours,
    totalDoubleOThours,
  };
};

// Hook to parse the preloaded forms for the time tracking grid
export default function useTimeTrackingGridPreloadSectionParser({
  divisionUserMap,
  divisionEquipmentMap,
  divisionCostcodeMap,
}) {
  const preloadSectionParser = useCallback(({
    sourceLabel,
    sections,
    date,
    columns = [],
    rows = [],
  }) => {
    const relevantValues = [...rows];
    const relevantColumns = [...columns];
    const validDataTypes = new Set(['TimeEntry', 'Equipment']);
    const costcodePhaseProjectMap = {};

    // Create a map of costcode, phase, and project to rowId
    columns.forEach((column) => {
      const {
        costcode: {
          id: costcodeId,
        },
        phase: {
          id: phaseId,
        },
        project: {
          id: projectId,
        },
      } = column;

      costcodePhaseProjectMap[`${costcodeId || ''}-${phaseId || ''}-${projectId || ''}`] = column.rowId;
    });

    const relevantValueMap = getIdMap(relevantValues);

    const ensureUnassignedColumn = () => {
      const key = '--';
      if (!costcodePhaseProjectMap[key]) {
        costcodePhaseProjectMap[key] = 'unassigned';
        relevantColumns.push({
          rowId: 'unassigned',
          costcode: {
            fullText: 'Unassigned',
            id: null,
          },
          phase: {
            name: null,
            id: null,
          },
          project: {
            name: null,
            id: null,
          },
        });
      }
      return costcodePhaseProjectMap[key];
    };

    const checkAndCreateColumn = ({
      costcodeId,
      phaseId,
      projectId,
      costcodeName,
      costcodeCode,
      phase,
      project,
    }) => {
      // Only create the unassigned column when an entry has no project and no cost code
      if (!costcodeId && !projectId) {
        return ensureUnassignedColumn();
      }

      const costcodePhaseProject = `${costcodeId || ''}-${phaseId || ''}-${projectId || ''}`;

      // Create column if it doesn't exist
      if (!costcodePhaseProjectMap[costcodePhaseProject]) {
        const generatedRowId = uuid();
        costcodePhaseProjectMap[costcodePhaseProject] = generatedRowId;
        relevantColumns.push({
          rowId: generatedRowId,
          costcode: {
            fullText: costcodeName ? `${costcodeCode} ${costcodeName}` : 'Unassigned',
            id: costcodeId,
          },
          phase: {
            name: phase,
            id: phaseId,
          },
          project: {
            name: project,
            id: projectId,
          },
        });
      }

      return costcodePhaseProjectMap[costcodePhaseProject];
    };

    sections.forEach(({ fields = [] }) => {
      fields.forEach(({ response: { values = [], dataType } = {} }) => {
        if (!validDataTypes.has(dataType)) {
          return;
        }

        // Parse equipment data
        if (dataType === 'Equipment') {
          values.forEach((value) => {
            if (!divisionEquipmentMap[value.equipmentId]) return;
            const existingRow = relevantValueMap[value.equipmentId];
            const costcode = divisionCostcodeMap[value.costcodeId];

            const relevantKey = checkAndCreateColumn({
              costcodeId: costcode ? value.costcodeId : null,
              phaseId: null,
              projectId: null,
              costcodeName: costcode?.name,
              costcodeCode: costcode?.code,
              phase: null,
              project: null,
            })

            if (existingRow) {
              const newHours = (parseFloat(value.hours) || 0)
                + (parseFloat(existingRow[relevantKey]?.hours) || 0);
              existingRow[relevantKey] = {
                hours: newHours.toFixed(2),
              };
            } else {
              const newValue = {
                ...divisionEquipmentMap[value.equipmentId],
                isPreloaded: true,
                sourceForm: sourceLabel,
                [relevantKey]: {
                  hours: value.hours ? parseFloat(value.hours).toFixed(2) : '0.00',
                },
                type: 'equipment',
              };
              relevantValues.push(newValue);
              relevantValueMap[value.equipmentId] = newValue;
            }
          });
          return;
        }

        // Parse time entry data
        values.forEach((value) => {
          const {
            costcode_code: costcodeCode,
            costcode_name: costcodeName,
            costcodeId,
            phase,
            phaseId,
            project,
            projectId,
            userId,
            date: valueDate,
            id: taskId,
          } = value;

          // If the user doesn't exist or the date doesn't match, skip
          if (!divisionUserMap[userId] || valueDate !== date) return;
          const rowId = checkAndCreateColumn({
            costcodeId,
            phaseId,
            projectId,
            costcodeName,
            costcodeCode,
            phase,
            project,
          });

          const {
            totalRegHours,
            totalOtHours,
            totalDoubleOThours,
          } = getHours(value, "preloadSectionParser");

          // Find the existing row
          const existingRow = relevantValueMap[value.userId];
          // If the row exists, update the existing values
          if (existingRow) {
            const existingValues = existingRow[rowId] || {};
            const newHours = addHours(totalRegHours, existingValues.hours);
            const newOtHours = addHours(totalOtHours, existingValues.otHours);
            const newDoubleOThours = addHours(totalDoubleOThours, existingValues.doubleOTHours);

            existingRow[rowId] = {
              hours: newHours.toFixed(2),
              otHours: newOtHours.toFixed(2),
              doubleOTHours: newDoubleOThours.toFixed(2),
              taskId,
            };
          } else {
            const newValue = {
              ...divisionUserMap[value.userId],
              isPreloaded: true,
              sourceForm: sourceLabel,
              [rowId]: {
                hours: totalRegHours.toFixed(2),
                otHours: totalOtHours.toFixed(2),
                doubleOTHours: totalDoubleOThours.toFixed(2),
                taskId,
              },
              type: 'user',
            };
            // If the row doesn't exist, create a new row
            relevantValues.push(newValue);
            relevantValueMap[value.userId] = newValue;
          }
        });
      });
    });

    return {
      relevantValues,
      relevantColumns,
    };
  }, [divisionUserMap, divisionEquipmentMap, divisionCostcodeMap]);

  return preloadSectionParser;
}
